import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaBriefcase, 
  FaSearch, 
  FaEye, 
  FaCheck, 
  FaTimes,
  FaUser,
  FaEnvelope,
  FaBuilding,
  FaGlobe,
  FaCalendarAlt,
  FaDollarSign,
  FaClock,
  FaTag
} from 'react-icons/fa';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { toast } from 'react-toastify';

const AdminHireRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await apiService.getHireRequests();
      console.log('Hire requests:', response.data);
      
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data && typeof response.data === 'object') {
        data = response.data.data || [];
      }
      
      setRequests(data);
    } catch (error) {
      console.error('Error fetching hire requests:', error);
      toast.error('Failed to fetch hire requests');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await apiService.updateHireStatus(id, status);
      toast.success(`Request ${status} successfully`);
      fetchRequests();
    } catch (error) {
      toast.error('Failed to update request status');
    }
  };

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    reviewing: 'bg-blue-500/20 text-blue-400',
    accepted: 'bg-green-500/20 text-green-400',
    rejected: 'bg-red-500/20 text-red-400',
    archived: 'bg-gray-500/20 text-gray-400'
  };

  const statusLabels = {
    pending: 'Pending',
    reviewing: 'Reviewing',
    accepted: 'Accepted',
    rejected: 'Rejected',
    archived: 'Archived'
  };

  const filteredRequests = requests.filter(request => {
    const matchesSearch = 
      request.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.request_type?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || request.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Hire Requests</h1>
          <p className="text-gray-400 mt-1">Manage all hiring and project requests</p>
        </div>
        <div className="text-sm text-gray-400">
          Total: {requests.length} requests
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search requests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="reviewing">Reviewing</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Requests List */}
      <div className="space-y-4">
        {filteredRequests.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400">No hire requests found.</p>
          </div>
        ) : (
          filteredRequests.map((request) => (
            <motion.div
              key={request.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-2xl p-6 hover:bg-white/10 transition-all duration-300"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center text-primary-400">
                      <FaBriefcase />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">{request.full_name}</h3>
                      <p className="text-gray-400 text-sm">{request.email}</p>
                    </div>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${statusColors[request.status] || statusColors.pending}`}>
                      {statusLabels[request.status] || request.status}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400">
                    {request.request_type && (
                      <span className="flex items-center gap-1.5">
                        <FaTag className="text-xs" />
                        {request.request_type}
                      </span>
                    )}
                    {request.company && (
                      <span className="flex items-center gap-1.5">
                        <FaBuilding className="text-xs" />
                        {request.company}
                      </span>
                    )}
                    {request.country && (
                      <span className="flex items-center gap-1.5">
                        <FaGlobe className="text-xs" />
                        {request.country}
                      </span>
                    )}
                    {request.budget && (
                      <span className="flex items-center gap-1.5">
                        <FaDollarSign className="text-xs" />
                        {request.budget}
                      </span>
                    )}
                    {request.timeline && (
                      <span className="flex items-center gap-1.5">
                        <FaClock className="text-xs" />
                        {request.timeline}
                      </span>
                    )}
                  </div>

                  {request.requirements && (
                    <p className="text-gray-300 text-sm mt-2 line-clamp-2">
                      <span className="font-medium">Requirements:</span> {request.requirements}
                    </p>
                  )}

                  <p className="text-xs text-gray-500 mt-2">
                    Submitted: {new Date(request.created_at).toLocaleString()}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setSelectedRequest(request);
                      setShowDetails(true);
                    }}
                    className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 text-gray-400 hover:text-primary-400"
                    title="View Details"
                  >
                    <FaEye size={14} />
                  </button>
                  
                  {request.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleStatusUpdate(request.id, 'reviewing')}
                        className="px-3 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 transition-all duration-300 text-blue-400 text-sm"
                      >
                        Review
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(request.id, 'accepted')}
                        className="px-3 py-1.5 rounded-lg bg-green-500/20 hover:bg-green-500/30 transition-all duration-300 text-green-400 text-sm flex items-center gap-1.5"
                      >
                        <FaCheck size={12} />
                        Accept
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(request.id, 'rejected')}
                        className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 transition-all duration-300 text-red-400 text-sm flex items-center gap-1.5"
                      >
                        <FaTimes size={12} />
                        Reject
                      </button>
                    </>
                  )}
                  
                  {request.status === 'reviewing' && (
                    <>
                      <button
                        onClick={() => handleStatusUpdate(request.id, 'accepted')}
                        className="px-3 py-1.5 rounded-lg bg-green-500/20 hover:bg-green-500/30 transition-all duration-300 text-green-400 text-sm flex items-center gap-1.5"
                      >
                        <FaCheck size={12} />
                        Accept
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(request.id, 'rejected')}
                        className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 transition-all duration-300 text-red-400 text-sm flex items-center gap-1.5"
                      >
                        <FaTimes size={12} />
                        Reject
                      </button>
                    </>
                  )}
                  
                  {request.status !== 'archived' && request.status !== 'rejected' && request.status !== 'accepted' && (
                    <button
                      onClick={() => handleStatusUpdate(request.id, 'archived')}
                      className="px-3 py-1.5 rounded-lg bg-gray-500/20 hover:bg-gray-500/30 transition-all duration-300 text-gray-400 text-sm"
                    >
                      Archive
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Details Modal */}
      {showDetails && selectedRequest && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-dark-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Request Details</h2>
              <button
                onClick={() => setShowDetails(false)}
                className="p-2 rounded-lg hover:bg-white/10 transition-all duration-300 text-gray-400"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400">Name</label>
                  <p className="text-white font-medium">{selectedRequest.full_name}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Email</label>
                  <p className="text-white font-medium">{selectedRequest.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400">Request Type</label>
                  <p className="text-white font-medium">{selectedRequest.request_type || 'Not specified'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Status</label>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${statusColors[selectedRequest.status] || statusColors.pending}`}>
                    {statusLabels[selectedRequest.status] || selectedRequest.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400">Company</label>
                  <p className="text-white font-medium">{selectedRequest.company || 'Not specified'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Country</label>
                  <p className="text-white font-medium">{selectedRequest.country || 'Not specified'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400">Budget</label>
                  <p className="text-white font-medium">{selectedRequest.budget || 'Not specified'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Timeline</label>
                  <p className="text-white font-medium">{selectedRequest.timeline || 'Not specified'}</p>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-400">Priority</label>
                <p className="text-white font-medium capitalize">{selectedRequest.priority || 'Normal'}</p>
              </div>

              <div>
                <label className="text-sm text-gray-400">Meeting Preference</label>
                <p className="text-white font-medium">{selectedRequest.meeting_preference || 'Not specified'}</p>
              </div>

              <div>
                <label className="text-sm text-gray-400">Requirements</label>
                <p className="text-white mt-1 whitespace-pre-wrap">{selectedRequest.requirements}</p>
              </div>

              {selectedRequest.attachments && selectedRequest.attachments.length > 0 && (
                <div>
                  <label className="text-sm text-gray-400">Attachments</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedRequest.attachments.map((file, i) => (
                      <span key={i} className="px-3 py-1 text-sm rounded bg-white/5 border border-white/10 text-gray-400">
                        📎 {file}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm text-gray-400">Submitted</label>
                <p className="text-white font-medium">{new Date(selectedRequest.created_at).toLocaleString()}</p>
              </div>

              {selectedRequest.notes && (
                <div>
                  <label className="text-sm text-gray-400">Notes</label>
                  <p className="text-white mt-1">{selectedRequest.notes}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-white/10">
                {selectedRequest.status === 'pending' && (
                  <>
                    <button
                      onClick={() => {
                        handleStatusUpdate(selectedRequest.id, 'reviewing');
                        setShowDetails(false);
                      }}
                      className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400"
                    >
                      Start Review
                    </button>
                    <button
                      onClick={() => {
                        handleStatusUpdate(selectedRequest.id, 'accepted');
                        setShowDetails(false);
                      }}
                      className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-400"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => {
                        handleStatusUpdate(selectedRequest.id, 'rejected');
                        setShowDetails(false);
                      }}
                      className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400"
                    >
                      Reject
                    </button>
                  </>
                )}
                <button
                  onClick={() => setShowDetails(false)}
                  className="px-4 py-2 border border-white/10 rounded-lg text-gray-400 hover:bg-white/5"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminHireRequests;